import { useInfiniteQuery, useTRPC } from "@luckymenu/api-client/react";
import {
	AUDIT_ACTIONS,
	type AuditAction,
	formatPaise,
	type StaffAuditEntry,
	type StaffAuditListInput,
} from "@luckymenu/core";
import { keepPreviousData } from "@tanstack/react-query";
import { useMemo } from "react";
import { useCan } from "./use-navigation";

/** The activity log's filters as the screen holds them. Empty is no filter. */
export type ActivityFilter = {
	/** A person's user ID. */
	person: string;
	action: AuditAction | "";
	/** Dates, YYYY-MM-DD, both included. */
	from: string;
	to: string;
};

export const NO_ACTIVITY_FILTER: ActivityFilter = { person: "", action: "", from: "", to: "" };

const DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Actions a restaurant's log can hold: every one but the platform's own. */
export const RESTAURANT_AUDIT_ACTIONS: readonly AuditAction[] = AUDIT_ACTIONS.filter(
	(a) => !/^(platformAdmin|plan|platformSettings)\./.test(a),
);

export const isAuditAction = (action: string): action is AuditAction =>
	(AUDIT_ACTIONS as readonly string[]).includes(action);

/**
 * A short line from an entry's data, its plain values only, for owners:
 * "billNo: SG2627-000001 · reason: Wrong table · amount: ₹300". Amounts in
 * paise (`*Paise`) read as rupees, and internal IDs (`id`, `*Id`, `*Ids`)
 * are left out: they mean nothing to the person reading the log.
 */
export function auditDataLine(data: Record<string, unknown>): string {
	return Object.entries(data)
		.filter(([, v]) => v !== null && v !== undefined && typeof v !== "object")
		.filter(([k]) => k !== "id" && !/Ids?$/.test(k))
		.map(([k, v]) =>
			/.Paise$/.test(k) && typeof v === "number"
				? `${k.slice(0, -"Paise".length)}: ${formatPaise(v)}`
				: `${k}: ${String(v)}`,
		)
		.join(" · ");
}

/** The filters as `staff.audit.list` takes them; a start after the end is left out until fixed. */
export function activityListInput(
	filter: ActivityFilter,
	limit = 50,
): Omit<StaffAuditListInput, "cursor"> {
	const from = DATE.test(filter.from) ? filter.from : undefined;
	const to = DATE.test(filter.to) ? filter.to : undefined;
	const both = from && to && from > to ? {} : { ...(from ? { from } : {}), ...(to ? { to } : {}) };
	return {
		limit,
		...(filter.person ? { actorUserId: filter.person } : {}),
		...(filter.action ? { action: filter.action } : {}),
		...both,
	};
}

/**
 * More > Activity log (P11-T11): who did what in the restaurant, newest
 * first, 50 at a time, by person, action and dates. Owners and managers
 * (`audit.view`). `people` fills the person filter.
 */
export function useActivityLog(filter: ActivityFilter) {
	const trpc = useTRPC();
	const allowed = useCan("audit.view");
	const input = activityListInput(filter);
	const query = useInfiniteQuery({
		...trpc.staff.audit.list.infiniteQueryOptions(input, {
			getNextPageParam: (page) => page.nextCursor ?? undefined,
		}),
		enabled: allowed,
		placeholderData: keepPreviousData,
	});
	const entries: StaffAuditEntry[] = useMemo(
		() => query.data?.pages.flatMap((p) => p.items) ?? [],
		[query.data],
	);
	return {
		status: query.data
			? ("ready" as const)
			: query.isError
				? ("error" as const)
				: ("loading" as const),
		entries,
		people: query.data?.pages[0]?.people ?? [],
		/** The dates are the wrong way round. */
		datesInvalid: DATE.test(filter.from) && DATE.test(filter.to) && filter.from > filter.to,
		hasMore: query.hasNextPage,
		loadingMore: query.isFetchingNextPage,
		loadMore: () => void query.fetchNextPage(),
		retry: () => void query.refetch(),
	};
}
